import api from './api';

// ---------------------------------------------------------------------------
// 🧾 ماژول حسابداری — آینهٔ figano-backend/src/services/adminAccounting.service.ts
// ---------------------------------------------------------------------------

export interface PnlLine { code: string; title: string; amount: number; count?: number; note: string; drill?: Record<string, string> }

export interface ProfitAndLoss {
  range: { from: string; to: string };
  revenue: PnlLine[];
  expenses: PnlLine[];
  totalRevenue: number;
  totalExpense: number;
  netProfit: number;
  pendingCommission: { amount: number; count: number; note: string };
  passThrough: PnlLine[];
  cashFlow: { moneyIn: number; moneyInCount: number; moneyOut: number; gatewayRefunds: number; sellerPayouts: number; userPayouts: number; moneyNet: number; note: string };
  withdrawals: { count: number; gross: number; fee: number; net: number };
}

export interface VatRow {
  key: string; label: string; firstDay: string; storeId?: string;
  items: number; ordersCount: number; sales: number; salesExVat: number; tax: number;
  refundedGoods: number; refundedTax: number; refundedItems: number; netTax: number; avgRate: number; legacyItems: number;
  bannerGross: number; bannerTax: number;
}
export interface VatReport {
  range: { from: string; to: string };
  groupBy: 'month' | 'day' | 'store';
  currentRate: number;
  avgRate: number;
  rows: VatRow[];
  totals: Omit<VatRow, 'key' | 'label' | 'firstDay' | 'avgRate' | 'storeId'>;
  notes: string[];
}

export interface SalesJournalRow {
  _id: string; orderId: string; orderNumber: string; paidAt: string; userId: string; storeId: string; storeName: string;
  productId: string; productTitle: string; quantity: number; fulfillmentType?: string; status: string;
  gross: number; discount: number; couponCode?: string; couponAuto?: boolean; couponAmount: number; volumeDiscount: number;
  net: number; tax: number; taxRate: number; legacyTax: boolean; shipping: number; inspectionFee: number; inspectorShip: number;
  sellerNet: number; refunded: number; refundedTax: number; active: boolean;
  gateway?: string | null; orderSplit: boolean; orderStatus: string; orderPaymentStatus: string; orderTotal: number; orderPayable: number; surcharge: number;
  deliveredAt?: string | null; disputeStatus?: string | null; obligationStatus?: string | null;
  buyer?: { firstName?: string; lastName?: string; phoneNumber?: string } | null;
}
export interface SalesJournal {
  range: { from: string; to: string };
  rows: SalesJournalRow[];
  total: number; page: number; limit: number;
  totals: { items: number; ordersCount: number; gross: number; discount: number; net: number; tax: number; shipping: number; inspectionFee: number; sellerNet: number; refunded: number; refundedTax: number; inactiveNet: number; inactiveItems: number } | null;
}

export interface BalanceLine { code: string; title: string; amount: number; note: string; count?: number }
export interface BalanceSheet {
  generatedAt: string;
  assets: BalanceLine[]; liabilities: BalanceLine[];
  totalAssets: number; totalLiabilities: number; equity: number;
  check: { cumulativeProfit: number; expectedEquity: number; difference: number; note: string; vatIncludedInLiabilities: number };
  payables: { generatedAt: string; fromCache: boolean; storesCount: number; durationMs: number };
  userWithdrawalsPending: { count: number; amount: number; note: string };
  cash: { in: number; inCount: number; gatewayRefunds: number; sellerPayoutsNet: number; sellerPayoutsGross: number; sellerFees: number; userPayouts: number };
}

export interface SellerPayableRow {
  storeId: string; storeName: string; ownerPhone?: string;
  available: number; escrow: number; awaitingDelivery: number; blocked: number; pendingWithdrawals: number; obligations: number; debt: number;
  totalEarnings: number; totalWithdrawn: number; inspectionAvailable: number; payable: number; error?: string;
}
export interface SellerPayables {
  generatedAt: string; fromCache: boolean; storesCount: number; durationMs: number;
  totals: Omit<SellerPayableRow, 'storeId' | 'storeName' | 'ownerPhone' | 'error'>;
  rows: SellerPayableRow[];
}

export interface CommissionRow {
  storeId: string; storeName: string; customCommission: number | null; hasReferrer: boolean;
  count: number; gross: number; fee: number; net: number; salePortion: number; inspectionPortion: number;
  effectiveRate: number; avgFeePct: number | null; zarinpalCount: number; manualCount: number; referralPaid: number; lastAt: string;
}
export interface CommissionReport {
  range: { from: string; to: string };
  rows: CommissionRow[];
  totals: { count: number; gross: number; fee: number; net: number; referralPaid: number; zarinpalCount: number };
  globalRate: number;
}

export interface ReconCheck { key: string; title: string; severity: 'critical' | 'warning' | 'info'; count: number; amount?: number; rows: any[]; hint: string; durationMs: number }
export interface Reconciliation { generatedAt: string; summary: { critical: number; warning: number; info: number; failed: number }; checks: ReconCheck[] }

export interface Invoice {
  order: {
    id: string; orderNumber: string; createdAt: string; paidAt: string | null; status: string; paymentStatus: string;
    subtotal: number; totalDiscount: number; subtotalAfterDiscount: number; tax: number; shippingCost: number; totalAmount: number; payableAmount: number; surcharge: number; refundedTotal: number;
    payment: { method?: string; provider?: string; refId?: string; rrn?: string; maskedPan?: string; paidAmount?: number; split: null | { status: string; bnplGateway?: string; legs: any[] } };
    volumeDiscounts: any[];
  };
  buyer: { name: string; phoneNumber?: string; email?: string } | null;
  stores: Array<{ storeId: string; storeName: string; phone?: string; owner?: string; address?: string; postalCode?: string; items: SalesJournalRow[]; totals: { gross: number; discount: number; net: number; tax: number; exVat: number; shipping: number; inspectionFee: number; sellerNet: number; refunded: number } }>;
  items: SalesJournalRow[];
  totals: { gross: number; discount: number; net: number; tax: number; exVat: number; shipping: number; inspectionFee: number; sellerNet: number; refunded: number; refundedTax: number };
  gatewayLogs: any[]; walletTransactions: any[]; obligations: any[];
  issuedAt: string;
}

export interface PeriodCloseSummary {
  id: string; periodKey: string; label: string; from: string; to: string; notes?: string; closedByName?: string; createdAt: string;
  totalRevenue: number; totalExpense: number; netProfit: number; netTax: number; equity: number; totalLiabilities: number;
}

export const PNL_LABELS = {
  activeItemStatus: { pending: 'در انتظار', processing: 'در حال پردازش', delivered: 'تحویل‌شده', cancelled: 'لغوشده', disputed: 'اختلاف', refunded: 'مسترد‌شده' } as Record<string, string>,
  orderStatus: { pending_payment: 'در انتظار پرداخت', processing: 'در حال پردازش', partially_delivered: 'تحویل جزئی', delivered: 'تحویل‌شده', completed: 'تکمیل', cancelled: 'لغوشده', disputed: 'اختلاف', refunded: 'مسترد‌شده' } as Record<string, string>,
  gateway: { zarinpal: 'زرین‌پال', snapppay: 'اسنپ‌پی', digipay: 'دیجی‌پی', payping: 'پی‌پینگ', wallet: 'کیف پول', mock: 'آزمایشی', card: 'کارت' } as Record<string, string>,
  severity: { critical: 'بحرانی', warning: 'هشدار', info: 'اطلاعاتی' } as Record<string, string>,
};

function buildQuery(params: Record<string, any>): string {
  const parts: string[] = [];
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return;
    parts.push(`${k}=${encodeURIComponent(Array.isArray(v) ? v.join(',') : String(v))}`);
  });
  return parts.length ? `?${parts.join('&')}` : '';
}


/** دخالت دستی ادمین در وجه فروشنده — برای شفافیت «کی چه کرد» */
export interface FundIntervention {
  _id: string;
  createdAt: string;
  type: 'escrow_released' | 'escrow_held' | 'escrow_policy_changed';
  storeId: string;
  storeName: string;
  amount: number;
  title: string;
  description?: string;
  orderId?: string;
  orderNumber?: string;
  productTitle?: string;
  actorName: string;
  bucketFrom?: string;
  bucketTo?: string;
  itemCount?: number;
}
export interface FundInterventions {
  rows: FundIntervention[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  byType: { type: string; count: number; amount: number }[];
  byActor: { actorName: string; count: number; amount: number; last: string }[];
}

const financeAccountingService = {
  getPnl: (params: { from?: string; to?: string }) => api.get<ProfitAndLoss>(`/admin/finance/accounting/pnl${buildQuery(params)}`),
  getVat: (params: { from?: string; to?: string; groupBy?: 'month' | 'day' | 'store'; storeId?: string }) => api.get<VatReport>(`/admin/finance/accounting/vat${buildQuery(params)}`),
  getSalesJournal: (params: { from?: string; to?: string; storeId?: string; userId?: string; gateway?: string; status?: string; search?: string; page?: number; limit?: number }) =>
    api.get<SalesJournal>(`/admin/finance/accounting/sales-journal${buildQuery(params)}`),
  exportSalesJournal: async (params: Record<string, any>) => {
    const blob = await api.get<Blob>(`/admin/finance/accounting/sales-journal/export${buildQuery(params)}`, { responseType: 'blob' } as any);
    const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob as any]));
    const a = document.createElement('a');
    a.href = url; a.download = `sales-journal-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  },
  getBalanceSheet: (refresh = false) => api.get<BalanceSheet>(`/admin/finance/accounting/balance-sheet${refresh ? '?refresh=1' : ''}`),
  getSellerPayables: (refresh = false) => api.get<SellerPayables>(`/admin/finance/accounting/seller-payables${refresh ? '?refresh=1' : ''}`),
  getCommissions: (params: { from?: string; to?: string }) => api.get<CommissionReport>(`/admin/finance/accounting/commissions${buildQuery(params)}`),
  getReconciliation: (sample = 15) => api.get<Reconciliation>(`/admin/finance/accounting/reconciliation?sample=${sample}`),
  getFundInterventions: (params: { from?: string; to?: string; storeId?: string; type?: string; page?: number; limit?: number } = {}) =>
    api.get<FundInterventions>(`/admin/finance/accounting/fund-interventions${buildQuery(params)}`),
  getInvoice: (orderId: string) => api.get<Invoice>(`/admin/finance/accounting/invoice/${orderId}`),
  listPeriodCloses: () => api.get<PeriodCloseSummary[]>('/admin/finance/accounting/period-closes'),
  getPeriodClose: (id: string) => api.get<any>(`/admin/finance/accounting/period-closes/${id}`),
  closePeriod: (body: { periodKey: string; label: string; from: string; to: string; notes?: string }) => api.post<any>('/admin/finance/accounting/period-closes', body),
};

export default financeAccountingService;
